import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link as LinkIcon, Trash2, X } from 'lucide-react'
import { Link } from 'react-router-dom'
import toast from 'react-hot-toast'
import { useAuthStore } from '../../store/auth'
import { waApi } from '../../api/whatsapp'
import { leadsApi } from '../../api/leads'
import { customStatusesApi } from '../../api/settings'
import { DropdownSelect } from '../../components/ui/DropdownSelect'
import { fullName } from '../../lib/utils'
import { LeadSearchInput } from '../mail/LeadSearchInput'

// LeadSearchInput works with a Lead OBJECT (not an id). Its Lead type isn't exported.
interface Lead { id: string; firstName: string; lastName: string; email: string }

export interface ChatDetailsPanelProps {
  companyId: string
  chatId: string
  onClose: () => void
}

export function ChatDetailsPanel({ companyId, chatId, onClose }: ChatDetailsPanelProps) {
  const qc = useQueryClient()
  const { user } = useAuthStore()
  const isOwner = user?.role === 'company_owner' || user?.role === 'administrator'
  const [note, setNote] = useState('')
  const [picked, setPicked] = useState<Lead | null>(null)

  const { data: chat } = useQuery({
    queryKey: ['wa-chat', companyId, chatId],
    queryFn: () => waApi.chatDetail(companyId, chatId),
  })
  const { data: notes = [] } = useQuery({
    queryKey: ['wa-notes', companyId, chatId],
    queryFn: () => waApi.listNotes(companyId, chatId),
  })
  const { data: members = [] } = useQuery({
    queryKey: ['wa-group-members', companyId, chatId],
    enabled: !!chat?.isGroup,
    queryFn: () => waApi.groupMembers(companyId, chatId),
  })
  // Read-only lead snapshot (values already masked server-side per canSeePhone/canSeeEmail).
  const { data: lead } = useQuery({
    queryKey: ['lead', companyId, chat?.leadId],
    enabled: !!chat?.leadId,
    queryFn: () => leadsApi.get(companyId, chat!.leadId!).then(r => r.data),
  })
  const { data: leadStatuses = [] } = useQuery({
    queryKey: ['custom-statuses', companyId],
    enabled: !!chat?.leadId,
    staleTime: 5 * 60 * 1000,
    queryFn: () => customStatusesApi.list(companyId).then(r => r.data.filter(s => s.applyTo === 'lead')),
  })
  const updateStatus = useMutation({
    mutationFn: (status: string) => leadsApi.update(companyId, chat!.leadId!, { status }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lead', companyId, chat?.leadId] })
      qc.invalidateQueries({ queryKey: ['leads', companyId] })
      toast.success('Status updated')
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to update status'),
  })

  const invalidateChat = () => {
    qc.invalidateQueries({ queryKey: ['wa-chat', companyId, chatId] })
    qc.invalidateQueries({ queryKey: ['wa-chats', companyId] })
  }
  const invalidateNotes = () => qc.invalidateQueries({ queryKey: ['wa-notes', companyId, chatId] })

  const link = useMutation({
    mutationFn: (leadId: string | null) => waApi.linkLead(companyId, chatId, leadId),
    onSuccess: () => { setPicked(null); invalidateChat() },
    onError: () => toast.error('Failed to update linked lead'),
  })
  const addNote = useMutation({
    mutationFn: () => waApi.createNote(companyId, chatId, note.trim()),
    onSuccess: () => { setNote(''); invalidateNotes() },
    onError: () => toast.error('Failed to add note'),
  })
  const removeNote = useMutation({
    mutationFn: (noteId: string) => waApi.deleteNote(companyId, noteId),
    onSuccess: invalidateNotes,
    onError: () => toast.error('Failed to delete note'),
  })
  if (!chat) return <div className="w-72 shrink-0 border-l" />

  return (
    <div className="flex w-72 shrink-0 flex-col gap-4 overflow-y-auto border-l p-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold">Chat details</h3>
        <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={16} /></button>
      </div>

      <section>
        <div className="label flex items-center gap-1"><LinkIcon size={13} /> Linked lead</div>
        {chat.lead ? (
          <div className="flex items-center justify-between rounded border p-2 text-sm">
            <Link to={`/company/${companyId}/leads/${chat.lead.id}`} className="font-medium text-blue-600 hover:underline">
              {fullName(chat.lead.firstName, chat.lead.lastName)}
            </Link>
            {isOwner && (
              <button className="btn btn-ghost btn-sm" disabled={link.isPending} onClick={() => link.mutate(null)}>
                Unlink
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            <LeadSearchInput companyId={companyId} value={picked} onChange={setPicked} placeholder="Search lead…" />
            <button
              className="btn btn-primary btn-sm w-full"
              disabled={!picked || link.isPending}
              onClick={() => picked && link.mutate(picked.id)}
            >
              Link to lead
            </button>
          </div>
        )}
      </section>

      {chat.isGroup && (
        <section>
          <div className="label">Members ({members.length})</div>
          <div className="space-y-1 rounded border p-2 text-sm">
            {members.length === 0 && <div className="text-gray-400">Loading members…</div>}
            {members.map((m, i) => (
              <div key={i} className="flex items-center justify-between">
                <span className="text-gray-700">
                  {m.name ? <>{m.name} {m.number && <span className="text-gray-400">{m.number}</span>}</> : (m.number ?? 'Unknown')}
                </span>
                {m.isAdmin && <span className="rounded bg-gray-100 px-1.5 text-xs text-gray-500">admin</span>}
              </div>
            ))}
          </div>
        </section>
      )}

      {chat.lead && lead && (
        <section>
          <div className="label">Lead details</div>
          <div className="space-y-1.5 rounded border p-2 text-sm">
            <DetailRow label="Name" value={fullName(lead.firstName, lead.lastName)} />
            <DetailRow label="Email" value={lead.email} />
            <DetailRow label="Phone" value={lead.phone} />
            {/* WhatsApp number of this chat — may differ from the lead's stored phone.
                Masked per canSeeWhatsapp (chat.number) vs canSeePhone (lead.phone). */}
            {!chat.isGroup && <DetailRow label="WhatsApp" value={chat.number} />}
            <DetailRow label="Organization" value={lead.organization} />
            <div className="flex items-center gap-2">
              <span className="w-24 shrink-0 text-gray-400">Status</span>
              <div className="min-w-0 flex-1">
                <DropdownSelect
                  value={lead.status ?? ''}
                  onChange={v => { if (v && v !== lead.status) updateStatus.mutate(v) }}
                  options={leadStatuses.map(s => ({ value: s.label, label: s.label }))}
                  searchable={leadStatuses.length > 6}
                />
              </div>
            </div>
            <DetailRow label="Source" value={lead.source} />
            <DetailRow label="Owner" value={lead.assignedUser ? `${lead.assignedUser.firstName} ${lead.assignedUser.lastName}` : null} />
          </div>
          <Link to={`/company/${companyId}/leads/${lead.id}`} className="mt-1 inline-block text-xs text-blue-600 hover:underline">
            Open full lead →
          </Link>
        </section>
      )}

      <section className="flex min-h-0 flex-1 flex-col">
        <div className="label">Notes</div>
        <div className="space-y-2">
          <textarea className="input" rows={2} placeholder="Add a note…" value={note} onChange={e => setNote(e.target.value)} />
          <button className="btn btn-primary btn-sm" disabled={!note.trim() || addNote.isPending} onClick={() => addNote.mutate()}>
            Add note
          </button>
        </div>
        <div className="mt-3 space-y-2 overflow-y-auto">
          {notes.map(n => (
            <div key={n.id} className="rounded border p-2 text-sm">
              <div className="flex items-center justify-between text-xs text-gray-500">
                <span>{n.authorName} · {new Date(n.createdAt).toLocaleString()}</span>
                {(n.userId === user?.id || isOwner) && (
                  <button disabled={removeNote.isPending} onClick={() => removeNote.mutate(n.id)}>
                    <Trash2 size={12} />
                  </button>
                )}
              </div>
              <div className="whitespace-pre-wrap">{n.content}</div>
            </div>
          ))}
        </div>
      </section>
    </div>
  )
}

function DetailRow({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="flex gap-2">
      <span className="w-24 shrink-0 text-gray-400">{label}</span>
      <span className="min-w-0 break-words text-gray-700">{value || '—'}</span>
    </div>
  )
}
